import { batch } from 'react-redux';
import { isEmpty, isNil } from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { message } from 'antd';
import api from '@/api';
import { NODE_TYPE } from '@/lib/nodeType';
import { roundByReference } from '@/shared/Chart/utils';
import { normalizeData } from '@/utils';
import { ON_THE_WHOLE_STRATUM, WITHOUT_STRATUM } from './additional-stratums';
import store from '@/store';

dayjs.extend(customParseFormat);

const DEFAULT_AXIS_PROPERTIES = {
    minBound: 0,
    maxBound: 0,
    majorTicksCount: 10,
    majorIntegerEnable: true,
    // FIXME: временно не используются
    // minorTicksCount: 10,
    // minorIntegerEnable: true,
};

const miningAndPumpingModel = {
    name: 'miningAndPumping',
    state: {
        initLoading: false,
        isRefreshing: false,
        lastRefreshAt: 0,
        axes: null,
        indicators: null,
        error: null,
        canceled: false,
        compareMode: false,
        compareModeWell: null,
        wellFinalState: null,
        drawerVisible: true,
        selectedDate: null,
        isFetching: false,
        chartsWidgetVisible: true,
        bsGisChartsWidgetVisible: false,
        tableWidgetVisible: true,
        miniMapWidgetVisible: false,
        gtmParametersWidgetVisible: false,
        resourcesTableWidgetVisible: false,
        fullScreenWidgetKey: null,
        exportLoading: false,
        drillingLiquidationDates: null,
        chartData: [],
        payload: null,
    },
    reducers: {
        setChartData(state, payload) {
            return {
                ...state,
                chartData: payload,
            };
        },
        setPayload(state, payload) {
            return {
                ...state,
                payload,
            };
        },
        setInitLoading(state, payload) {
            return { ...state, initLoading: payload };
        },
        setAxes(state, payload) {
            return { ...state, axes: payload };
        },
        setWellFinalState(state, payload) {
            return { ...state, wellFinalState: payload };
        },
        setIndicators(state, payload) {
            return { ...state, indicators: payload };
        },
        setIsFetching(state, payload) {
            return { ...state, isFetching: payload };
        },
        setIsRefreshing(state, payload) {
            return { ...state, isRefreshing: payload };
        },
        setLastRefreshAt(state, payload) {
            return { ...state, lastRefreshAt: payload };
        },
        setError(state, payload) {
            return { ...state, error: payload };
        },
        setCanceled(state, payload) {
            return { ...state, canceled: payload };
        },
        setCompareMode(state, payload) {
            return { ...state, compareMode: payload, compareModeWell: null };
        },
        setCompareModeWell(state, payload) {
            return { ...state, compareModeWell: payload };
        },
        setDrawerVisible(state, payload) {
            return { ...state, drawerVisible: payload };
        },
        setSelectedDate(state, payload) {
            return { ...state, selectedDate: payload };
        },
        setChartsWidgetVisible(state, payload) {
            let fullScreenWidgetKey = state.fullScreenWidgetKey;
            if (!payload && state.fullScreenWidgetKey === 'chart') {
                fullScreenWidgetKey = null;
            }
            return { ...state, chartsWidgetVisible: payload, fullScreenWidgetKey };
        },
        setBSGisChartsWidgetVisible(state, payload) {
            let fullScreenWidgetKey = state.fullScreenWidgetKey;
            if (!payload && state.fullScreenWidgetKey === 'gisChart') {
                fullScreenWidgetKey = null;
            }
            return { ...state, bsGisChartsWidgetVisible: payload, fullScreenWidgetKey };
        },
        setTableWidgetVisible(state, payload) {
            let fullScreenWidgetKey = state.fullScreenWidgetKey;
            if (!payload && state.fullScreenWidgetKey === 'table') {
                fullScreenWidgetKey = null;
            }
            return { ...state, tableWidgetVisible: payload, fullScreenWidgetKey };
        },
        setMiniMapWidgetVisible(state, payload) {
            let fullScreenWidgetKey = state.fullScreenWidgetKey;
            if (!payload && state.fullScreenWidgetKey === 'miniMap') {
                fullScreenWidgetKey = null;
            }
            return { ...state, miniMapWidgetVisible: payload, fullScreenWidgetKey };
        },
        setGtmParametersWidgetVisible(state, payload) {
            let fullScreenWidgetKey = state.fullScreenWidgetKey;
            if (!payload && state.fullScreenWidgetKey === 'gtmParameters') {
                fullScreenWidgetKey = null;
            }
            return { ...state, gtmParametersWidgetVisible: payload, fullScreenWidgetKey };
        },
        setResourcesTableWidgetVisible(state, payload) {
            let fullScreenWidgetKey = state.fullScreenWidgetKey;
            if (!payload && state.fullScreenWidgetKey === 'resourcesTable') {
                fullScreenWidgetKey = null;
            }
            return { ...state, resourcesTableWidgetVisible: payload, fullScreenWidgetKey };
        },
        setFullScreenWidgetKey(state, payload) {
            const newPayload = payload === state.fullScreenWidgetKey ? null : payload;
            return { ...state, fullScreenWidgetKey: newPayload };
        },
        setExportLoading(state, payload) {
            return { ...state, exportLoading: payload };
        },
        setDrillingLiquidationDates(state, payload) {
            return { ...state, drillingLiquidationDates: payload };
        },
    },

    effects: dispatch => ({
        async init(payload, rootState) {
            await this.setInitLoading(true);
            await this.clearData();
            const { calculationObject, activeItem } = payload;
            const byField = Boolean(calculationObject);
            const byWell = Boolean(activeItem);
            const { isFetching } = rootState.miningAndPumping;

            if (activeItem || calculationObject) {
                try {
                    dispatch.chartsWidget.setLoadingDataChart(true);
                    dispatch.tableWidget.setLoadingDataSource(true);
                    let result = null;
                    const params = await dispatch.tableWidget.loadParamsFromIDB({ isField: byField });

                    if (byWell) {
                        params.wellId = activeItem.id;
                        await dispatch.gdis.fetchData({
                            wellIds: [activeItem.id],
                        });

                        result = await this.fetchDataset({
                            body: params,
                        });
                    }
                    if (byField) {
                        if (calculationObject.node === 'field') {
                            if (calculationObject.parentVentureNode === NODE_TYPE.AREA) {
                                params.areaId = calculationObject.parentVentureId;
                            } else {
                                params.ventureId = calculationObject.parentVentureId;
                            }
                            params.fieldId = calculationObject?.id;

                            result = await this.fetchDataset({
                                body: params,
                            });
                        } else {
                            if (calculationObject.node === 'group' || calculationObject.node === 'shaft') {
                                const wellsId = [];
                                const versionIds = [0];
                                const allStratums = [];
                                console.log('calculationObject,', calculationObject);
                                const promises = calculationObject.children.map(async child => {
                                    const allWellVersions = await api.carottage.getAllWellVersions(child.id);
                                    if (!Array.isArray(allWellVersions) || allWellVersions.length === 0) {
                                        console.log(`No well versions found for child.id: ${child.id}`);
                                        return;
                                    }

                                    allWellVersions.forEach(version => {
                                        const childStratums = version.stratums
                                            .map(stratum => Number(stratum.boredStratumId))
                                            .filter(stratumId => !isNaN(stratumId));
                                        allStratums.push(...childStratums);
                                    });
                                });

                                await Promise.all(promises);

                                const uniqueStratums = Array.from(new Set(allStratums));

                                params.wellsId = calculationObject.children.map(child => child.id);
                                // params.stratums = uniqueStratums;
                                params.versionIds = versionIds;
                                const { wellId, ...clearedParams } = params;
                                console.log('clearedParams,', [clearedParams, wellsId]);
                                result = await this.fetchDataset({
                                    body: clearedParams,
                                });
                            }
                        }
                    }
                    // const extendedPayload = {
                    //     body: params,
                    // };

                    // const result = await this.fetchDataset(extendedPayload);
                    console.log('result', result);
                    if (isFetching) return;
                    if (isEmpty(result)) {
                        // message.error('Отсутствие данных');
                    } else {
                        await this.setResult({ result, byField });
                    }
                    dispatch.tableWidget.setLoadingDataSource(false);
                    dispatch.chartsWidget.setLoadingDataChart(false);
                    this.setInitLoading(false);
                } catch (error) {
                    if (error.code === 20) return;
                    console.error(error);
                    message.error('Не удалось получить начальные данные!');
                    dispatch.tableWidget.setLoadingDataSource(false);
                    dispatch.chartsWidget.setLoadingDataChart(false);
                    this.setInitLoading(false);
                }
            }
        },
        async setCompareModeWithReset(payload) {
            if (payload === false) {
                batch(() => {
                    this.setCompareMode(payload);
                    dispatch.chartsWidget.setLoadingDataChart(false);
                    dispatch.chartsWidget.setErrorMessage('');
                });
            } else {
                this.setCompareMode(payload);
            }
        },
        async setResult(payload, rootState) {
            try {
                const { result, byField } = payload;
                dispatch.miningAndPumping.setPayload(payload);
                await dispatch.tableWidget.loadLocalSettings({
                    isField: byField,
                    defaultSettings: result.settings.headers,
                });
                if (result?.characteristics?.OpFactorDB) {
                    result.characteristics.OpFactorDB.axis = 'OpFactorDB';
                    result.characteristics.OpFactorDB.typeDiagram = 'linear';
                }
                if (result?.characteristics?.OpFactorCalc) {
                    result.characteristics.OpFactorCalc.axis = 'OpFactorCalc';
                    result.characteristics.OpFactorCalc.typeDiagram = 'linear';
                }
                result.characteristics.GDISPressure = {
                    key: 'GDISPressure',
                    title: 'Пластовое давление(ГДИС)',
                    type: 'float',
                    accessory: ['production'],
                    destination: ['table', 'chart'],
                    accuracy: 3,
                    align: 'left',
                    axis: 'GDISPressure',
                    typeDiagram: 'linear',
                    orderNumber: 75,
                };
                if (result.characteristics.FactorInjectivity) {
                    result.characteristics.FactorInjectivity = {
                        ...result.characteristics.FactorInjectivity,
                        destination: ['table', 'chart'],
                        align: 'left',
                        axis: 'FactorInjectivity',
                        typeDiagram: 'linear',
                    };
                }

                await this.setIndicators(result.characteristics);
                await dispatch.chartsWidget.defineIndicatorsTemplate({ byField });
                const gdis = rootState.gdis.data;
                const additionalGdis = await dispatch.gdis.prepareDataForSettings(gdis);

                await dispatch.gtm.prepareGtmSettingsRequestData({
                    gtmChart: result.settings.chart_gtm,
                    additionalInfo: [
                        additionalGdis,
                        {
                            drillingLiquidationDates: {
                                data: result.data.drillingLiquidationDates,
                                key: 'drillingLiquidationDates',
                                title: 'Бурение / Ликвидация',
                            },
                        },
                    ],
                });
                let chartData = result.data.chart;
                dispatch.miningAndPumping.setChartData(chartData);
                if (result?.data?.GDIS && result.data.GDIS.length > 0) {
                    chartData = chartData.map(dataItem => {
                        if (result.data.GDIS.filter(item => item.stratumID === dataItem.stratumId)) {
                            const GDISPoints = result.data.GDIS.filter(
                                item => item.stratumID === dataItem.stratumId,
                            ).map(item => [
                                dayjs(item.eDate ?? item.sDate, 'DD.MM.YYYY').format('MM.YYYY'),
                                item.boreHolePress,
                            ]);
                            GDISPoints.sort((a, b) => a[1] - b[1]).sort(
                                (a, b) => dayjs(a[0], 'MM.YYYY').toDate() - dayjs(b[0], 'MM.YYYY').toDate(),
                            );
                            return { ...dataItem, data: { ...dataItem.data, GDISPressure: [GDISPoints] } };
                        }
                    });
                }

                const opFactorDBPoints = new Map();
                result.data.table.forEach(item => {
                    if (item?.OpFactorDB !== undefined) {
                        if (opFactorDBPoints.get(item.stratumId)) {
                            opFactorDBPoints.get(item.stratumId).push([item.PeriodDate, item.OpFactorDB]);
                        } else {
                            opFactorDBPoints.set(item.stratumId, [[item.PeriodDate, item.OpFactorDB]]);
                        }
                    }
                });
                opFactorDBPoints.entries().forEach(([stratumId, points]) => {
                    const stratumExist = chartData.find(dataByStratum => dataByStratum.stratumId === stratumId);
                    if (stratumExist) {
                        stratumExist.data = { ...stratumExist.data, OpFactorDB: [points] };
                    } else {
                        chartData.push({ stratumId: stratumId, data: { OpFactorDB: [points] } });
                    }
                });
                const opFactorCalcPoints = new Map();
                result.data.table.forEach(item => {
                    if (item?.OpFactorCalc !== undefined) {
                        if (opFactorCalcPoints.get(item.stratumId)) {
                            opFactorCalcPoints.get(item.stratumId).push([item.PeriodDate, item.OpFactorCalc]);
                        } else {
                            opFactorCalcPoints.set(item.stratumId, [[item.PeriodDate, item.OpFactorCalc]]);
                        }
                    }
                });
                opFactorCalcPoints.entries().forEach(([stratumId, points]) => {
                    const stratumExist = chartData.find(dataByStratum => dataByStratum.stratumId === stratumId);
                    if (stratumExist) {
                        stratumExist.data = { ...stratumExist.data, OpFactorCalc: [points] };
                    } else {
                        chartData.push({ stratumId: stratumId, data: { OpFactorCalc: [points] } });
                    }
                });
                const factorInjectivityPoints = new Map();
                result.data.table.forEach(item => {
                    if (item?.FactorInjectivity !== undefined) {
                        if (factorInjectivityPoints.get(item.stratumId)) {
                            factorInjectivityPoints.get(item.stratumId).push([item.PeriodDate, item.FactorInjectivity]);
                        } else {
                            factorInjectivityPoints.set(item.stratumId, [[item.PeriodDate, item.FactorInjectivity]]);
                        }
                    }
                });
                factorInjectivityPoints.entries().forEach(([stratumId, points]) => {
                    const stratumExist = chartData.find(dataByStratum => dataByStratum.stratumId === stratumId);
                    if (stratumExist) {
                        stratumExist.data = { ...stratumExist.data, FactorInjectivity: [points] };
                    } else {
                        chartData.push({ stratumId: stratumId, data: { FactorInjectivity: [points] } });
                    }
                });
                batch(() => {
                    dispatch.versions.initializeVersions({ data: result.settings.versions });
                    dispatch.miningAndPumping.configureStratums(result.settings.stratums);
                    dispatch.tableWidget.setSettingsDefaultTemplateColumns(result.settings.headers);
                    dispatch.tableWidget.setDataSource(result.data.table);
                    dispatch.chartsWidget.setDataChart(chartData);
                    dispatch.gtm.setData(result.data.gtm);
                    this.setWellFinalState(result.data.wellFinalState);
                    dispatch.tableWidget.convertSettingsRawColumns({ data: result.characteristics });

                    this.configureAxes({
                        axes: {
                            ...result.settings.axes,
                            OpFactorDB: {
                                key: 'OpFactorDB',
                                title: 'Коэффициент продуктивности (из БД)',
                                isVisible: true,
                                orient: 'left',
                            },
                            OpFactorCalc: {
                                key: 'OpFactorCalc',
                                title: 'Коэффициент продуктивности (рассчитанный)',
                                isVisible: true,
                                orient: 'left',
                            },
                            FactorInjectivity: {
                                key: 'FactorInjectivity',
                                title: 'Коэфф. прием.',
                                isVisible: true,
                                orient: 'left',
                            },
                            GDISPressure: {
                                key: 'GDISPressure',
                                title: 'Пластовое давление',
                                isVisible: true,
                                orient: 'left',
                            },
                        },
                        chartData: chartData,
                    });
                });
            } catch (e) {
                console.error('Something went wrong in miningAndPumping.setResult: ', e);
                throw new Error(String(e));
            }
        },

        async fetchData(payload, rootState) {
            const { initLoading, isFetching } = rootState.miningAndPumping;
            const { groupPeriod, groupSubtotal, groupTotal, activeStratums } = rootState.tableWidget;
            const { active: versionsPriority } = rootState.versions;

            const { calculationObject, activeItem, isVersions = false } = payload;
            const byField = Boolean(calculationObject);
            const byWell = Boolean(activeItem);
            if (isFetching) {
                return;
            }
            if (calculationObject && (calculationObject.node === 'group' || calculationObject.node === 'shaft')) return;

            if (!isEmpty(versionsPriority) && !initLoading && (byWell || byField)) {
                batch(() => {
                    dispatch.tableWidget.setLoadingDataSource(true);
                    dispatch.chartsWidget.setLoadingDataChart(true);
                    this.setCanceled(false);
                });

                const body = {
                    versionIds: (versionsPriority ?? []).map(v => Number(v)),
                    step: Number(groupPeriod),
                    total: Number(groupSubtotal),
                    sum: Boolean(groupTotal),
                };
                if (activeStratums) {
                    const activeStratumsIds = Object.entries(activeStratums).reduce((acc, [id, isActive]) => {
                        if (isActive) acc.push(Number(id));
                        return acc;
                    }, []);
                    body.stratums = activeStratumsIds;
                }

                if (byWell) body.wellId = Number(activeItem.id);
                if (byField) {
                    const parentId = Number(calculationObject.parentVentureId);
                    if (calculationObject.parentVentureNode === NODE_TYPE.AREA && parentId) {
                        body.areaId = parentId;
                    } else if (parentId) {
                        body.ventureId = parentId;
                    }
                    body.fieldId = Number(calculationObject.id);
                }

                const extendedPayload = {
                    body,
                };

                try {
                    dispatch.chartsWidget.setErrorMessage('');

                    const result = await this.fetchDataset(extendedPayload);

                    if (isFetching) return;
                    if (isEmpty(result)) {
                        // message.error('Отсутствие данных');
                    } else {
                        if (isVersions) {
                            dispatch.miningAndPumping.configureStratums(result.settings.stratums);
                            this.configureAxes({ axes: result.settings.axes, chartData: result.data.chart });
                        }
                        dispatch.tableWidget.setDataSource(result.data.table);
                        dispatch.chartsWidget.setDataChart(result.data.chart);
                        dispatch.gtm.setData(result.data.gtm);
                    }
                    dispatch.tableWidget.setLoadingDataSource(false);
                    dispatch.chartsWidget.setLoadingDataChart(false);
                } catch (error) {
                    message.error('Не удалось получить данные');
                    console.error(error);
                    batch(() => {
                        dispatch.chartsWidget.setErrorMessage('Ошибка при загрузке данных.');
                        dispatch.tableWidget.setDataSource(null);
                        dispatch.chartsWidget.setDataChart(null);
                    });
                } finally {
                    this.setIsFetching(false);
                    dispatch.tableWidget.setLoadingDataSource(false);
                    dispatch.chartsWidget.setLoadingDataChart(false);
                }
            }
        },

        /**
         * Очистка кэша и повторный запрос данных
         * @param {Object} payload - не используется, получаем данные из rootState
         * @param {Object} rootState - Redux state
         */
        async refreshData(payload, rootState) {
            // Если уже идёт обновление — выполним префлайт и покажем detail (если есть), без запуска второго цикла
            if (rootState.miningAndPumping?.isRefreshing) {
                try {
                    const { calculationObject } = rootState.tree;
                    const { activeItem, activeItemKey, data: listsData } = rootState.lists;
                    const { active: versionsPriority } = rootState.versions;
                    const { groupPeriod, groupSubtotal, groupTotal, activeStratums } = rootState.tableWidget;

                    const body = {
                        versionIds: (versionsPriority ?? []).map(v => Number(v)),
                        step: Number(groupPeriod),
                        total: Number(groupSubtotal),
                        sum: Boolean(groupTotal),
                    };
                    if (activeStratums) {
                        body.stratums = Object.entries(activeStratums).reduce(
                            (acc, [id, on]) => (on ? (acc.push(Number(id)), acc) : acc),
                            [],
                        );
                    }
                    let activeItemLocal = activeItem;
                    if (!activeItemLocal && activeItemKey && Array.isArray(listsData)) {
                        const findItemByKey = (nodes, key) => {
                            for (const node of nodes || []) {
                                if (node?.key === key) return node;
                                if (node?.children) {
                                    const found = findItemByKey(node.children, key);
                                    if (found) return found;
                                }
                            }
                            return null;
                        };
                        activeItemLocal = findItemByKey(listsData, activeItemKey);
                    }
                    if (activeItemLocal) body.wellId = Number(activeItemLocal.id);
                    if (calculationObject) {
                        const parentId = Number(calculationObject.parentVentureId);
                        if (calculationObject.parentVentureNode === NODE_TYPE.AREA && parentId) body.areaId = parentId;
                        else if (parentId) body.ventureId = parentId;
                        body.fieldId = Number(calculationObject.id);
                    }
                    const pre = await api.productionInjection.fetchData({ body });
                    if (pre?.detail) message.warning(pre.detail);
                } catch (_) {}
                return;
            }

            // Троттлинг старта нового обновления, чтобы избежать частых запусков
            try {
                const nowTs = Date.now();
                const lastTs = Number(rootState.miningAndPumping?.lastRefreshAt || 0);
                const MIN_INTERVAL_MS = 1500;
                if (lastTs && nowTs - lastTs < MIN_INTERVAL_MS) {
                    return;
                }
                this.setLastRefreshAt(nowTs);
            } catch (_) {}
            this.setIsRefreshing(true);
            const { calculationObject } = rootState.tree;
            const { activeItem, activeItemKey, data: listsData } = rootState.lists;
            const { active: versionsPriority } = rootState.versions;
            const { groupPeriod, groupSubtotal, groupTotal, activeStratums } = rootState.tableWidget;

            try {
                // Формируем и нормализуем body
                const body = {
                    versionIds: (versionsPriority ?? []).map(v => Number(v)),
                    step: Number(groupPeriod),
                    total: Number(groupSubtotal),
                    sum: Boolean(groupTotal),
                };

                if (activeStratums) {
                    const activeStratumsIds = Object.entries(activeStratums).reduce((acc, [id, isActive]) => {
                        if (isActive) acc.push(Number(id));
                        return acc;
                    }, []);
                    body.stratums = activeStratumsIds;
                }

                // Пытаемся восстановить activeItem по ключу, если объект отсутствует в сторе
                let activeItemLocal = activeItem;
                if (!activeItemLocal && activeItemKey && Array.isArray(listsData)) {
                    const findItemByKey = (nodes, key) => {
                        if (!Array.isArray(nodes)) return null;
                        for (const node of nodes) {
                            if (node?.key === key) return node;
                            if (node?.children) {
                                const found = findItemByKey(node.children, key);
                                if (found) return found;
                            }
                        }
                        return null;
                    };
                    activeItemLocal = findItemByKey(listsData, activeItemKey);
                }

                const byWell = Boolean(activeItemLocal);
                const byField = Boolean(calculationObject);

                if (byWell) body.wellId = Number(activeItemLocal.id);
                if (byField) {
                    const parentId = Number(calculationObject.parentVentureId);
                    if (calculationObject.parentVentureNode === NODE_TYPE.AREA && parentId) {
                        body.areaId = parentId;
                    } else if (parentId) {
                        body.ventureId = parentId;
                    }
                    body.fieldId = Number(calculationObject.id);
                }

                // Валидация обязательного контекста и версий
                const hasWell = typeof body.wellId === 'number' && !Number.isNaN(body.wellId);
                const hasField = typeof body.fieldId === 'number' && !Number.isNaN(body.fieldId);

                if (!hasWell && !hasField) {
                    this.setIsRefreshing(false);
                    return;
                }

                const validVersions = Array.isArray(body.versionIds) && body.versionIds.length > 0;
                if (!validVersions) {
                    this.setIsRefreshing(false);
                    return;
                }

                // Префлайт: всегда сначала обращаемся к эндпоинту; если пришёл detail — показываем и выходим
                let preflight;
                try {
                    preflight = await api.productionInjection.fetchData({ body });
                } catch (e) {
                    console.warn(e);
                }
                if (preflight?.detail) {
                    message.warning(preflight.detail);
                    this.setIsRefreshing(false);
                    return;
                }
                // расчёта нет — удаляем кэш
                try {
                    await api.productionInjection.deleteCache({ body });
                } catch (e) {
                    console.warn(e);
                }

                // 2. Поллинг повторного запроса данных до готовности
                const maxAttempts = 10;
                const delayMs = 2000;

                // Отображаем загрузку виджетов на время поллинга
                dispatch.tableWidget.setLoadingDataSource(true);
                dispatch.chartsWidget.setLoadingDataChart(true);

                let success = false;
                for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
                    let result = null;
                    try {
                        result = await api.productionInjection.fetchData({ body });
                    } catch (e) {
                        console.warn(e);
                    }

                    // Если API возвращает payload с данными, считаем это валидным ответом
                    const dataPayload = result?.payload ?? result;
                    const hasDetail = typeof result?.detail === 'string' && result.detail.length > 0;
                    if (result?.message && result.message === 'Ошибка запроса') {
                        message.error('Ошибка запроса. Проверьте параметры.');
                        break;
                    }

                    if (dataPayload && !hasDetail) {
                        if (!isEmpty(dataPayload)) {
                            const byField = Boolean(calculationObject);
                            if (byWell) {
                                const wellIdForGdis = Number((activeItemLocal && activeItemLocal.id) || body.wellId);
                                if (!Number.isNaN(wellIdForGdis)) {
                                    try {
                                        await dispatch.gdis.fetchData({ wellIds: [wellIdForGdis] });
                                    } catch (e) {
                                        console.warn(e);
                                    }
                                }
                            }
                            await this.setResult({ result: dataPayload, byField });
                        }
                        success = true;
                        break;
                    }

                    // Если сервер сообщает о расчёте — ждём и пробуем снова
                    await new Promise(resolve => setTimeout(resolve, delayMs));
                }

                // Снимаем индикаторы загрузки
                dispatch.tableWidget.setLoadingDataSource(false);
                dispatch.chartsWidget.setLoadingDataChart(false);
            } catch (error) {
                console.error('Error refreshing data:', error);
                // Не шумим ошибкой при обновлении
            } finally {
                this.setIsRefreshing(false);
            }
        },

        async fetchDataset(payload, state) {
            let result = null;
            const { canceled, isFetching, initLoading } = state.miningAndPumping;

            if (
                !isFetching ||
                (!initLoading &&
                    payload.body.versionIds &&
                    payload.body.versionIds[0] === 0 &&
                    payload.body.versionIds.length > 1)
            ) {
                dispatch.tableWidget.setLoadingDataSource(true);
                dispatch.chartsWidget.setLoadingDataChart(true);

                try {
                    dispatch.chartsWidget.setErrorMessage('');
                    this.setIsFetching(true);
                    result = await api.productionInjection.fetchData(payload);

                    // Проверка на специальный ответ о расчете
                    const hasDetail = typeof result?.detail === 'string' && result.detail.length > 0;
                    if (hasDetail) {
                        message.warning(result.detail);
                        this.setIsFetching(false);
                        dispatch.tableWidget.setLoadingDataSource(false);
                        dispatch.chartsWidget.setLoadingDataChart(false);
                        return null;
                    }

                    // Поддержка формата { payload, message }
                    const dataPayload = result?.payload ?? result;
                    if (result?.message && !isEmpty(result.message)) {
                        message.info(result.message);
                    }

                    if (!result) throw new Error('No result received');
                } catch (error) {
                    console.error('Error in fetchDataset:', error);

                    dispatch.chartsWidget.setErrorMessage('Ошибка при загрузке данных.');
                    this.setIsFetching(false);
                    if (canceled) {
                        console.warn('Fetch operation was canceled.');
                    }
                } finally {
                    if (result) this.setIsFetching(false);
                }
                return result;
            }
        },

        /**
         * Retry fetching data
         * @param {Object} payload - The request payload
         * @param {Object} rootState - The Redux state
         */
        async retryFetchData(payload, state) {
            const { data } = state.versions;

            if (state.canceled) return;

            const calculationObject = state.tree?.calculationObject ?? null;
            const activeItem = state.lists?.activeItem ?? null;
            const effectivePayload = payload ?? { calculationObject, activeItem };

            if (isEmpty(data)) {
                await dispatch.miningAndPumping.init(effectivePayload);
            } else {
                await dispatch.miningAndPumping.fetchData(effectivePayload);
            }
        },

        cancelRequest(_, rootState) {
            if (!rootState.canceled) {
                this.setCanceled(true);
                batch(() => {
                    dispatch.tableWidget.setLoadingDataSource(false);
                    dispatch.chartsWidget.setLoadingDataChart(false);
                });
            }
        },

        resetCancellation() {
            this.setCanceled(false);
        },

        filterColumns(columns) {
            if (isNil(columns)) return null;
            const settings = Object.entries(columns).reduce((accSettings, [settingKey, settingData]) => {
                const groups = settingData.reduce((accGroups, groupData) => {
                    if (!groupData.isEnabled) return accGroups;

                    const group = { key: groupData.key, title: groupData.title };
                    if (groupData.children) {
                        const characteristics = groupData.children.reduce((accCharacteristics, characteristicData) => {
                            if (characteristicData.isEnabled) {
                                accCharacteristics.push({
                                    key: characteristicData.key,
                                    title: characteristicData.title,
                                });
                            }
                            return accCharacteristics;
                        }, []);
                        if (!isEmpty(characteristics)) group.children = characteristics;
                    }
                    accGroups.push(group);
                    return accGroups;
                }, []);
                if (isEmpty(groups)) return accSettings;
                accSettings[settingKey] = groups;

                return accSettings;
            }, {});
            if (isEmpty(settings)) return null;
            return settings;
        },

        configureStratums(stratums) {
            batch(() => {
                if (stratums.table && Array.isArray(stratums.table)) {
                    const stratumsTable = normalizeData(stratums.table).data;
                    const activeStratumsTable = stratums.table.reduce((acc, { id }) => ({ ...acc, [id]: true }), {});

                    dispatch.tableWidget.setStratums(stratumsTable);
                    dispatch.tableWidget.setActiveStratums(activeStratumsTable);
                }
                if (stratums.chart && Array.isArray(stratums.chart)) {
                    //FIXME: временно отключен пласт "Без пласта"
                    const countStratumns = stratums.chart.reduce(
                        (acc, { id }) => (id !== WITHOUT_STRATUM.id && id !== ON_THE_WHOLE_STRATUM.id ? ++acc : acc),
                        0,
                    );
                    const stratumsChartList = stratums.chart.filter(
                        ({ id }) => id !== WITHOUT_STRATUM.id && (id !== ON_THE_WHOLE_STRATUM.id || countStratumns > 1),
                    );
                    const stratumsChart = normalizeData(stratumsChartList).data;
                    const activeStratumsChart = stratumsChartList.reduce(
                        (acc, { id }) => ({
                            ...acc,
                            [id]: (countStratumns > 1 && id === ON_THE_WHOLE_STRATUM.id) || countStratumns <= 1,
                        }),
                        {},
                    );
                    activeStratumsChart[WITHOUT_STRATUM.id] = true;
                    /*
                    const stratumsChart = normalizeData(stratums.chart).data;
                    const activeStratumsChart = stratums.chart.reduce((acc, { id }) => ({ ...acc, [id]: false }), {});
                    */
                    dispatch.chartsWidget.setStratums(stratumsChart);
                    dispatch.chartsWidget.setActiveStratums(activeStratumsChart);
                }
            });
        },

        configureAxes({ axes, chartData }, rootState) {
            const { indicators } = rootState.miningAndPumping;
            const xAxisBounds = {
                minBound: Infinity,
                maxBound: -Infinity,
            };
            const boundsByAxisKey = chartData.reduce((acc, { data }) => {
                Object.entries(data ?? {}).forEach(([indicatorKey, points = []]) => {
                    const axisKey = indicators[indicatorKey].axis;
                    const flatedPoints = Array.isArray(points.flat()[0]) ? points.flat() : points;
                    const { minBound, maxBound } = flatedPoints.reduce(
                        (accPoints, point) => {
                            const [x = null, y = null] = point;
                            const xDate = dayjs(x, 'MM.YYYY');
                            xAxisBounds.minBound = Math.min(xAxisBounds.minBound, xDate);
                            xAxisBounds.maxBound = Math.max(xAxisBounds.maxBound, xDate);

                            accPoints.minBound = Math.min(accPoints.minBound, y);
                            accPoints.maxBound = Math.max(accPoints.maxBound, y);
                            return accPoints;
                        },
                        { minBound: Infinity, maxBound: -Infinity },
                    );
                    if (axisKey) {
                        acc[axisKey] ||= { minBound: Infinity, maxBound: -Infinity };
                        acc[axisKey].minBound = Math.min(acc[axisKey].minBound, minBound);
                        acc[axisKey].maxBound = Math.max(acc[axisKey].maxBound, maxBound);
                    }
                });
                return acc;
            }, {});
            if (!isNil(xAxisBounds.minBound)) xAxisBounds.minBound = dayjs(xAxisBounds.minBound).year();
            if (!isNil(xAxisBounds.maxBound)) xAxisBounds.maxBound = dayjs(xAxisBounds.maxBound).year();

            const activeAxes = {};
            const {
                minBound: minBoundDefault,
                maxBound: maxBoundDefault,
                ...defaultAxisProperties
            } = DEFAULT_AXIS_PROPERTIES;

            const updatedAxes = Object.values(axes ?? {}).reduce((acc, axis) => {
                let updatedAxis = axis;
                if (axis.key === 'Period') {
                    updatedAxis = { ...axis, ...xAxisBounds };
                } else {
                    let { minBound = minBoundDefault, maxBound = maxBoundDefault } = boundsByAxisKey[axis.key] ?? {};
                    [minBound, maxBound] = roundByReference(axis.key, [minBound, maxBound]);

                    updatedAxis = { ...axis, ...defaultAxisProperties, minBound, maxBound };

                    activeAxes[axis.key] = true;
                }
                acc[axis.key] = updatedAxis;
                return acc;
            }, {});
            this.setAxes(updatedAxes);
            dispatch.chartsWidget.setActiveAxes(activeAxes);
        },

        async onUnmount() {
            await this.cancelRequest();
            this.setInitLoading(true);
        },

        async clearData() {
            await this.cancelRequest();
            batch(() => {
                this.setAxes(null);
                this.setCompareMode(false);
                this.setCanceled(false);
                this.setSelectedDate(null);
                dispatch.chartsWidget.setCompareModeChartOptions(null);
                dispatch.chartsWidget.setActiveAxes(null);
                dispatch.chartsWidget.setStratums(null);
                dispatch.tableWidget.setStratums(null);
                dispatch.chartsWidget.setActiveStratums(null);
                dispatch.tableWidget.setActiveStratums(null);
                dispatch.chartsWidget.setIndicatorsTemplate(null);
                dispatch.chartsWidget.setIndicators(null);
                dispatch.tableWidget.setSettingsRawColumns(null);
                dispatch.chartsWidget.setDataChart(null);
                dispatch.tableWidget.setDataSource(null);
                dispatch.tableWidget.setColumns(null);
                dispatch.tableWidget.setColumnsWidth(null);

                dispatch.versions.resetVersionsToDefault();

                dispatch.chartsWidget.setLoadingDataChart(false);
                dispatch.tableWidget.setLoadingDataSource(false);
            });
        },
        async updateChartDataOnStratumChange() {
            const rootState = store.getState();
            const { result } = rootState.miningAndPumping.payload;
            const filteredGdis = await store.select.gdis.filteredGDISData(rootState);
            const additionalGdis = await dispatch.gdis.prepareDataForSettings(filteredGdis);
            await dispatch.gtm.prepareGtmSettingsRequestData({
                gtmChart: result.settings.chart_gtm,
                additionalInfo: [
                    additionalGdis,
                    {
                        drillingLiquidationDates: {
                            data: result.data.drillingLiquidationDates,
                            key: 'drillingLiquidationDates',
                            title: 'Бурение / Ликвидация',
                        },
                    },
                ],
            });
        },
    }),
    selectors: (slice, createSelector) => ({
        isFullScreen() {
            return createSelector(
                rootState => rootState.miningAndPumping.chartsWidgetVisible,
                rootState => rootState.miningAndPumping.tableWidgetVisible,
                rootState => rootState.miningAndPumping.bsGisChartsWidgetVisible,
                rootState => rootState.miningAndPumping.miniMapWidgetVisible,
                (chartsWidgetVisible, tableWidgetVisible, miniMapWidgetVisible, bsGisChartsWidgetVisible) => {
                    const arrayOfVisibles = [
                        chartsWidgetVisible,
                        tableWidgetVisible,
                        miniMapWidgetVisible,
                        bsGisChartsWidgetVisible,
                    ].filter(v => v);
                    return arrayOfVisibles.length === 1;
                },
            );
        },

        periodAxis() {
            return slice(({ axes }) => axes?.Period ?? null);
        },
    }),
};

export default miningAndPumpingModel;
